import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import { AccordionSummary, Typography, AccordionDetails, Grid, FormControlLabel, Checkbox, TextField, InputAdornment } from '@material-ui/core';
import { ReasonSelector } from '../../selectors/ReasonSelector';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { ReferredToSelector } from '../../selectors/ReferredToSelector';
import { IndividualNameSummary } from '../../selectors/IndividualSelector';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import GavelIcon from '@material-ui/icons/Gavel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { FieldSwitchButton } from '../components/FieldSwitchButton';
import type { EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('ProceedingPanel');

// Explicit request (EnquiryFormDrawer split): the "Proceeding" accordion, moved out of the drawer's inline
// getProceeding render function with its JSX and every field's name / validate / handler logic unchanged.
// The outer component subscribes to the form values and passes the memoised body only the values it
// displays; event handlers read form.getState().values when they fire.
export interface ProceedingPanelProps {
    form: FormApi<any>;
    classes: EnquiryFormClasses;
    isHidePanel: boolean;
    requiredValidator: FieldValidator<any>;
    handleExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean, form: FormApi<any>) => void;
    // tslint:disable-next-line: no-any
    onCheckBoxChanged: (form: FormApi<any>, values: AnyObject, input: any, checked: boolean, name: string) => void;
    onDropdownChange: (selection: IAutoCompleteItem, name: string, input: FieldInputProps<any, HTMLElement>, form: FormApi<any>, valueGuid: string) => void;
    // tslint:disable-next-line: no-any
    onDropDownSelect: (form: FormApi<any>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    onSwitch: (form: FormApi<any>, values: AnyObject, name: string) => void;
}

interface ProceedingPanelBodyProps extends ProceedingPanelProps {
    formValues: AnyObject;
}

const ProceedingPanelBody: React.FC<ProceedingPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isHidePanel, requiredValidator, handleExpandChange, onCheckBoxChanged, onDropdownChange, onDropDownSelect, onSwitch,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            defaultExpanded={true}
            // expanded={false}
            onChange={(event: React.ChangeEvent<{}>, expanded: boolean) => handleExpandChange(event, expanded, form)}
            className={isHidePanel ? classes.hideDate : classes.expansionPanel}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="proceeding-content"
                id="proceeding-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipTeal)}>
                        <GavelIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelTeal, classes.sectionLabelText)}>
                        Proceeding
                    </Typography>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="isNotProceeding"
                        >
                            {({ input, meta }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values.isNotProceeding}
                                            checkedIcon={<CheckBoxIcon className={classes.checkedIcon} />}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                                // Another approach.
                                                onCheckBoxChanged(form, form.getState().values, input, checked, 'isNotProceeding');
                                            }}
                                            name="isNotProceeding"
                                            disabled={values.assignedTo ? false : true}
                                        />
                                    }
                                    label="Not Proceeding"
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div>
                            <Grid item={true} xs={12}>
                                <Field
                                    name="notProceedingReason"
                                    validate={values.isNotProceeding === true && !isHidePanel ? requiredValidator : undefined}
                                    // validate={required}
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <ReasonSelector
                                            variant="outlined"
                                            size="small"
                                            {...input}
                                            label="Not Proceeding Reason"
                                            name="notProceedingReason"
                                            className={classes.textField}
                                            error={meta.error && meta.touched}
                                            disablePortal={false}
                                            onSelection={(selection: IAutoCompleteItem, name: string, reasonGuid: string) => {
                                                onDropdownChange(selection, name, input, form, reasonGuid);
                                            }}
                                            required={values.isNotProceeding === true && !isHidePanel ? true : false}
                                            // required={true}
                                            disabled={values.isNotProceeding === false ? true : false}
                                            helperText="This field is required"
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </div>
                    </Grid>
                    {values.isShowNotProceedingReferrerInProceeding && (
                        <Grid item={true} xs={6} className={`${classes.customFieldGrid} ${classes.displayFlex} ${classes.fieldRowGap}`}>
                            <div className={classes.toggleFieldWrapepr}>
                                <div className="field">
                                    {
                                        (values.isNotProceedingReferrerSwitchedInProceeding) ? (
                                            <Field
                                                name="referredToFirmNameInProceeding"
                                                // validate={required}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...input}
                                                        label="Referred To"
                                                        type="text"
                                                        // required={!isTabActivity()}
                                                        className={classes.textField}
                                                        error={
                                                            meta.error &&
                                                            meta.touched
                                                        }
                                                        autoComplete="abcd"
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Referred To is required'
                                                                : ''
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FieldSwitchButton
                                                                        tooltip="Switch to select an existing client & contact"
                                                                        onClick={() => onSwitch(form, form.getState().values, 'referredToFirmInProceeding')}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        ) :
                                            (
                                                <Field
                                                    name="referredToFirmSelectorInProceeding"
                                                    subscription={{ touched: true, error: true, value: true }}
                                                >
                                                    {({ input, meta }) => (
                                                        <ReferredToSelector
                                                            variant="outlined"
                                                            size="small"
                                                            {...input}
                                                            label="Referred To"
                                                            value={values.referredToFirmSelectorInProceeding}
                                                            error={meta.error && meta.touched}
                                                            helperText={
                                                                meta.error &&
                                                                    meta.touched
                                                                    ? 'Referred To is required'
                                                                    : ''
                                                            }
                                                            onSelection={(selection: IndividualNameSummary, name: string) => {
                                                                input.onChange(selection);
                                                                onDropDownSelect(form, form.getState().values, input, selection, name);
                                                            }}
                                                            endAdornmentExtra={(
                                                                <FieldSwitchButton
                                                                    tooltip="Switch to enter the referred to name"
                                                                    onClick={() => onSwitch(form, form.getState().values, 'referredToFirmInProceeding')}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </Field>
                                            )
                                    }
                                </div>
                            </div>
                        </Grid>
                    )}
                    <Grid item={true} xs={values.isShowNotProceedingReferrerInProceeding ? 6 : 12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="notProceedingOutcomeNotesInProceeding"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Not Proceeding Notes"
                                        type="text"
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        autoComplete="abcd"
                                        disabled={values.isNotProceeding === false ? true : false}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
ProceedingPanelBody.displayName = 'ProceedingPanelBody';

export const ProceedingPanel: React.FC<ProceedingPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <ProceedingPanelBody
                {...panelProps}
                formValues={{
                    isNotProceeding: values.isNotProceeding,
                    assignedTo: values.assignedTo,
                    isShowNotProceedingReferrerInProceeding: values.isShowNotProceedingReferrerInProceeding,
                    isNotProceedingReferrerSwitchedInProceeding: values.isNotProceedingReferrerSwitchedInProceeding,
                    referredToFirmSelectorInProceeding: values.referredToFirmSelectorInProceeding,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
ProceedingPanel.displayName = 'ProceedingPanel';
